class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.bindDrawerInteractions();
  }

  connectedCallback() {
    this.bindDrawerInteractions();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.closeBound === 'true') return;
    overlay.dataset.closeBound = 'true';
    overlay.addEventListener('click', () => this.close());
  }

  bindDrawerInteractions() {
    if (this.dataset.interactionsBound === 'true') return;
    this.dataset.interactionsBound = 'true';

    this.addEventListener('click', (event) => {
      const giftToggle = event.target.closest('[data-cart-gift-toggle]');
      if (giftToggle) {
        event.preventDefault();
        this.toggleGiftNote(giftToggle);
        return;
      }

      const upsellBtn = event.target.closest('[data-cart-upsell-add]');
      if (upsellBtn) {
        event.preventDefault();
        this.addUpsellProduct(upsellBtn);
        return;
      }

      const upgradeBtn = event.target.closest('.cart-drawer-item__upgrade');
      if (upgradeBtn) {
        event.preventDefault();
        this.updateSellingPlan(upgradeBtn);
      }
    });

    this.addEventListener('change', (event) => {
      if (event.target.matches('.cart-drawer-item__plan-select')) {
        this.updateSellingPlan(event.target);
      }
    });

    this.syncGiftNoteState();
  }

  syncGiftNoteState() {
    const toggle = this.querySelector('[data-cart-gift-toggle]');
    const expanded = toggle?.getAttribute('aria-expanded') === 'true';
    this.classList.toggle('gift-note-open', Boolean(expanded));
  }

  addUpsellProduct(button) {
    if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;

    const id = Number(button.dataset.variantId);
    const quantity = Number(button.dataset.quantity || 1);
    if (!id) return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');

    const payload = {
      id,
      quantity,
      sections: this.getSectionsPayload().join(','),
      sections_url: window.location.pathname,
    };
    if (button.dataset.sellingPlan) payload.selling_plan = Number(button.dataset.sellingPlan);

    fetch(`${routes.cart_add_url}`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = response.description || response.message || window.cartStrings.error;
          return;
        }

        this.classList.remove('is-empty');
        if (response.sections?.['cart-drawer']) {
          this.renderContents(response);
        } else {
          this.refreshFromSectionFetch();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: id,
          cartData: response,
        });
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
  }

  refreshFromSectionFetch() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceInner = html.querySelector('#CartDrawer');
        const targetInner = this.querySelector('#CartDrawer');
        if (sourceInner && targetInner) targetInner.innerHTML = sourceInner.innerHTML;
        this.classList.toggle('is-empty', Boolean(html.querySelector('cart-drawer.is-empty')));
        this.bindOverlay();
        this.syncGiftNoteState();
      });
  }

  toggleGiftNote(toggle) {
    const note = this.querySelector('[data-cart-gift-note]');
    if (!note) return;

    const textarea = note.querySelector('textarea');
    const willExpand = note.classList.contains('is-collapsed');
    note.classList.toggle('is-collapsed', !willExpand);
    toggle.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
    this.classList.toggle('gift-note-open', willExpand);

    if (textarea) {
      textarea.tabIndex = willExpand ? 0 : -1;
      textarea.setAttribute('aria-hidden', willExpand ? 'false' : 'true');
    }

    if (willExpand && textarea) {
      requestAnimationFrame(() => {
        textarea.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        setTimeout(() => {
          textarea.focus({ preventScroll: true });
          this.syncDrawerViewport();
        }, 80);
      });
    } else {
      textarea?.blur();
    }
  }

  isMobileDrawer() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  syncDrawerViewport = () => {
    if (!this.classList.contains('active') || !this.isMobileDrawer() || !window.visualViewport) {
      this.style.removeProperty('--cart-drawer-height');
      this.style.removeProperty('--cart-drawer-offset');
      return;
    }

    this.style.setProperty('--cart-drawer-height', `${window.visualViewport.height}px`);
    this.style.setProperty('--cart-drawer-offset', `${window.visualViewport.offsetTop}px`);

    if (this.classList.contains('gift-note-open')) {
      this.querySelector('.cart-drawer__gift-textarea')?.scrollIntoView({ block: 'nearest' });
    }
  };

  bindDrawerViewport() {
    if (this.dataset.viewportBound === 'true') return;
    this.dataset.viewportBound = 'true';
    window.visualViewport?.addEventListener('resize', this.syncDrawerViewport);
    window.visualViewport?.addEventListener('scroll', this.syncDrawerViewport);
    window.addEventListener('resize', this.syncDrawerViewport);
    this.syncDrawerViewport();
  }

  unbindDrawerViewport() {
    this.dataset.viewportBound = 'false';
    window.visualViewport?.removeEventListener('resize', this.syncDrawerViewport);
    window.visualViewport?.removeEventListener('scroll', this.syncDrawerViewport);
    window.removeEventListener('resize', this.syncDrawerViewport);
    this.style.removeProperty('--cart-drawer-height');
    this.style.removeProperty('--cart-drawer-offset');
  }

  getSectionsPayload() {
    return this.getSectionsToRender().map((section) => section.id);
  }

  refreshFromSections(parsedState) {
    this.classList.toggle('is-empty', Number(parsedState.item_count) === 0);

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;

      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html != null) sectionElement.innerHTML = html;
    });

    this.bindOverlay();
    this.syncGiftNoteState();
  }

  getSellingPlanValue(el) {
    // Buttons expose .value as "" — never use that for selling plan IDs
    if (el.tagName === 'SELECT') {
      return el.value;
    }
    return el.dataset.sellingPlan || '';
  }

  getCartJsUrl() {
    const cartUrl = routes.cart_url || '/cart';
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  }

  findCartLine(cart, el) {
    const key = el.dataset.lineKey;
    const line = Number(el.dataset.index);
    const variantId = Number(el.dataset.variantId);
    if (key) {
      const byKey = cart.items?.find((item) => item.key === key);
      if (byKey) return byKey;
    }
    if (line && cart.items?.[line - 1]) return cart.items[line - 1];
    if (variantId) return cart.items?.find((item) => Number(item.variant_id) === variantId) || null;
    return null;
  }

  sanitizeLineProperties(properties) {
    const clean = {};
    if (!properties || typeof properties !== 'object') return clean;
    Object.entries(properties).forEach(([key, value]) => {
      if (value == null || value === '') return;
      clean[key] = String(value);
    });
    return clean;
  }

  cartRequestFailed(data) {
    if (!data) return true;
    if (data.status && Number(data.status) >= 400) return true;
    if (data.errors) return true;
    if (data.message === 'Cart Error') return true;
    return false;
  }

  cartRequestErrorMessage(data, fallback = window.cartStrings.error) {
    if (typeof data?.description === 'string' && data.description) return data.description;
    if (typeof data?.errors === 'string' && data.errors) return data.errors;
    if (typeof data?.message === 'string' && data.message && data.message !== 'Cart Error') return data.message;
    return fallback;
  }

  showCartError(message, line) {
    const errorEl = line ? document.getElementById(`CartDrawer-LineItemError-${line}`) : null;
    const textEl = errorEl?.querySelector('.cart-item__error-text');
    if (textEl) {
      textEl.textContent = message;
      return;
    }
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = message;
  }

  async fetchCart() {
    const response = await fetch(this.getCartJsUrl());
    if (!response.ok) throw new Error(window.cartStrings.error);
    return response.json();
  }

  async postJson(url, payload, accept = 'json') {
    const response = await fetch(url, {
      ...fetchConfig(accept),
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch (error) {
      return { status: response.status || 500, message: 'Cart Error', description: window.cartStrings.error };
    }
  }

  async restoreCartLine(item) {
    if (!item?.variant_id || !item?.quantity) return;
    const payload = {
      id: Number(item.variant_id),
      quantity: Number(item.quantity),
    };
    const properties = this.sanitizeLineProperties(item.properties);
    if (Object.keys(properties).length) payload.properties = properties;
    const existingPlan = item.selling_plan_allocation?.selling_plan?.id;
    if (existingPlan) payload.selling_plan = Number(existingPlan);
    await this.postJson(routes.cart_add_url, payload, 'javascript');
  }

  updateSellingPlan(el) {
    const line = Number(el.dataset.index);
    const sellingPlan = this.getSellingPlanValue(el);
    const cartItems = this.querySelector('cart-drawer-items');

    if (!cartItems) return;
    if (el.tagName !== 'SELECT' && !sellingPlan) {
      this.showCartError(window.cartStrings.error, line);
      return;
    }

    el.setAttribute('aria-disabled', 'true');
    el.classList.add('is-loading');
    if (line) cartItems.enableLoading(line);

    this.applySellingPlan(el, sellingPlan)
      .then((cartData) => {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-selling-plan',
          cartData,
        });
      })
      .catch((error) => {
        this.showCartError(error.message || window.cartStrings.error, line);
      })
      .finally(() => {
        el.removeAttribute('aria-disabled');
        el.classList.remove('is-loading');
        if (line) cartItems.disableLoading(line);
      });
  }

  async applySellingPlan(el, sellingPlan) {
    const cart = await this.fetchCart();
    const item = this.findCartLine(cart, el);
    if (!item) throw new Error(window.cartStrings.error);

    const variantId = Number(el.dataset.variantId || item.variant_id);
    const quantity = Number(el.dataset.quantity || item.quantity || 1);
    const properties = this.sanitizeLineProperties(item.properties);

    const removed = await this.postJson(routes.cart_change_url, {
      id: item.key,
      quantity: 0,
    });
    if (this.cartRequestFailed(removed)) {
      throw new Error(this.cartRequestErrorMessage(removed));
    }

    const addPayload = {
      id: variantId,
      quantity,
      sections: this.getSectionsPayload().join(','),
      sections_url: window.location.pathname,
    };
    if (Object.keys(properties).length) addPayload.properties = properties;
    if (sellingPlan) addPayload.selling_plan = Number(sellingPlan);

    const added = await this.postJson(routes.cart_add_url, addPayload, 'javascript');
    if (this.cartRequestFailed(added) || !added.id) {
      await this.restoreCartLine(item);
      await this.refreshFromSectionFetch();
      throw new Error(this.cartRequestErrorMessage(added));
    }

    await this.refreshFromSectionFetch();
    return added;
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.bindDrawerViewport();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    this.unbindDrawerViewport();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.bindOverlay();
      this.syncGiftNoteState();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
